'use client';

import { Sparkles, Wand2, Check, X } from 'lucide-react';

interface MagicalStoryModalProps {
  isOpen: boolean;
  storyIdea: string;
  onProceed: () => void;
  onCancel: () => void;
}

export function MagicalStoryModal({ isOpen, storyIdea, onProceed, onCancel }: MagicalStoryModalProps) {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        animation: 'fadeIn 0.3s ease-out',
      }}
    >
      {/* Backdrop with blur */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(12px)',
          animation: 'fadeIn 0.3s ease-out',
        }}
        onClick={onCancel}
      />

      {/* Floating magical particles */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${4 + Math.random() * 8}px`,
              height: `${4 + Math.random() * 8}px`,
              background: `hsl(${Math.random() * 360}, 80%, 70%)`,
              borderRadius: '50%',
              opacity: 0.6,
              animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 3}s`,
              filter: 'blur(1px)',
              boxShadow: '0 0 10px currentColor',
            }}
          />
        ))}
      </div>

      {/* Modal Card */}
      <div
        style={{
          position: 'relative',
          maxWidth: '600px',
          width: '100%',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 250, 245, 0.98) 100%)',
          borderRadius: '24px',
          padding: '2.5rem',
          boxShadow: '0 20px 60px rgba(168, 216, 230, 0.5), 0 10px 30px rgba(201, 182, 228, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.8)',
          animation: 'modalSlideUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        }}
      >
        {/* Magical glow border */}
        <div
          style={{
            position: 'absolute',
            inset: -2,
            background: 'linear-gradient(135deg, var(--color-sky-blue), var(--color-soft-purple), var(--color-peach))',
            borderRadius: '24px',
            opacity: 0.6,
            filter: 'blur(8px)',
            zIndex: -1,
            animation: 'rotateBorder 8s linear infinite',
          }}
        />

        {/* Sparkle icon with animation */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, var(--color-sky-blue), var(--color-soft-purple))',
              borderRadius: '50%',
              marginBottom: '1rem',
              animation: 'pulse 2s ease-in-out infinite, rotate 10s linear infinite',
              boxShadow: '0 8px 32px rgba(168, 216, 230, 0.6), 0 0 40px rgba(201, 182, 228, 0.4)',
            }}
          >
            <Wand2 size={40} color="white" style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))' }} />
          </div>

          <h2
            style={{
              fontSize: 'clamp(1.75rem, 5vw, 2.25rem)',
              fontWeight: 800,
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-soft-purple))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '0.75rem',
              letterSpacing: '-0.02em',
            }}
          >
            ✨ Magic Story Created! ✨
          </h2>

          <p
            style={{
              fontSize: '1rem',
              color: 'var(--color-text-secondary)',
              marginBottom: '0',
              fontWeight: 500,
            }}
          >
            You didn't input anything, so we conjured up an amazing story for you!
          </p>
        </div>

        {/* Story idea box with scroll */}
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(168, 216, 230, 0.1), rgba(201, 182, 228, 0.1))',
            border: '2px solid var(--color-border)',
            borderRadius: '16px',
            padding: '1.5rem',
            marginBottom: '2rem',
            maxHeight: '300px',
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          {/* Floating sparkles in box */}
          <div style={{ position: 'absolute', top: '10px', right: '10px', opacity: 0.4 }}>
            <Sparkles size={20} color="var(--color-soft-purple)" style={{ animation: 'twinkle 2s ease-in-out infinite' }} />
          </div>
          <div style={{ position: 'absolute', bottom: '10px', left: '10px', opacity: 0.4 }}>
            <Sparkles size={16} color="var(--color-sky-blue)" style={{ animation: 'twinkle 2s ease-in-out infinite 1s' }} />
          </div>

          <p
            style={{
              fontSize: '1.0625rem',
              lineHeight: '1.7',
              color: 'var(--color-text-primary)',
              margin: 0,
              fontWeight: 500,
            }}
          >
            {storyIdea}
          </p>
        </div>

        {/* Action buttons */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1rem',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '1rem 1.5rem',
              fontSize: '1.0625rem',
              fontWeight: 600,
              background: 'white',
              color: 'var(--color-text-secondary)',
              border: '2px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: 'var(--shadow-sm)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-primary)';
              e.currentTarget.style.color = 'var(--color-primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
            }}
          >
            <X size={20} />
            <span>Write My Own</span>
          </button>

          <button
            onClick={onProceed}
            className="btn-primary"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '1rem 1.5rem',
              fontSize: '1.0625rem',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                animation: 'shimmer 3s ease-in-out infinite',
              }}
            />
            <Check size={20} />
            <span style={{ position: 'relative', zIndex: 1 }}>Proceed with This!</span>
          </button>
        </div>

        {/* Helper text */}
        <p
          style={{
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
            marginTop: '1rem',
            marginBottom: 0,
          }}
        >
          Love this story? Click "Proceed" to create your magical book! 🎨
        </p>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translateY(50px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0) translateX(0);
          }
          33% {
            transform: translateY(-20px) translateX(10px);
          }
          66% {
            transform: translateY(10px) translateX(-10px);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes rotateBorder {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes twinkle {
          0%,
          100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.2);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(200%);
          }
        }

        @media (max-width: 640px) {
          div[style*='gridTemplateColumns'] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

